import React, { useState } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { Inventory2Outlined } from "@mui/icons-material";
import { ProductImage } from "../../types/productImage";

// Single source of truth for "which image represents this product" —
// reused by ProductThumbnail below and anywhere else (e.g. a Chip avatar)
// that needs just the URL rather than a rendered box.
export function getProductThumbnailUrl(
    images?: ProductImage[] | null,
): string | undefined {
    if (!images || images.length === 0) return undefined;
    return (images.find((img) => img.is_default) || images[0]).thumbnail_url;
}

interface ProductThumbnailProps {
    images?: ProductImage[] | null;
    alt?: string;
    size?: number;
    sx?: SxProps<Theme>;
}

// Small square product photo with a neutral fallback icon — used anywhere
// staff scan a list of products to recognize one (search dropdowns, the
// order-import review screen). Every consumer of productService.getProducts
// already receives `images` at no extra cost (eager-loaded server-side),
// so this never triggers an additional request.
export const ProductThumbnail: React.FC<ProductThumbnailProps> = ({
    images,
    alt = "",
    size = 36,
    sx,
}) => {
    const [errored, setErrored] = useState(false);
    const src = getProductThumbnailUrl(images);

    const baseSx: SxProps<Theme> = {
        width: size,
        height: size,
        borderRadius: 1,
        flexShrink: 0,
        ...sx,
    };

    if (!src || errored) {
        return (
            <Box
                sx={{
                    ...baseSx,
                    bgcolor: "action.hover",
                    color: "text.disabled",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Inventory2Outlined sx={{ fontSize: size * 0.55 }} />
            </Box>
        );
    }

    return (
        <Box
            component="img"
            src={src}
            alt={alt}
            loading="lazy"
            onError={() => setErrored(true)}
            sx={{
                ...baseSx,
                objectFit: "cover",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
            }}
        />
    );
};
